import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Leaf, Sprout, BadgeCheck, Drumstick, Star } from "lucide-react";
import { Reveal, StaggerGroup, StaggerItem, MaskLine } from "../components/Motion";
import { ComboCard } from "../components/ComboCard";
import { ChooseYourCurry } from "../components/ChooseYourCurry";
import GoogleReviews from "../components/GoogleReviews";
import { useOrder } from "../components/OrderProvider";
import { assets, business } from "../data/site";
import { combos } from "../data/menu";
import SEO from "../components/SEO";

const badges = [
  { icon: Leaf, label: "Vegetarian" },
  { icon: Sprout, label: "Vegan" },
  { icon: BadgeCheck, label: "100% Halal" },
  { icon: Drumstick, label: "Non-Veg" },
];

export default function Home() {
  const openOrder = useOrder();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <div data-testid="home-page">
      <SEO
        route="home"
        fallbackTitle="Shaan Curry House | Authentic Indian Food & Catering in Ottawa"
        fallbackDescription="Ottawa's beloved Indian kitchen since 1995. Veg, Vegan, 100% Halal & Non-Veg. Dine in, combo meals and full catering on Merivale Road."
      />
      {/* ============ HERO ============ */}
      <section ref={heroRef} className="relative min-h-[92vh] flex items-end overflow-hidden">
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
          <img
            src={assets.gallery[7]}
            alt="Authentic Indian feast at Shaan Curry House"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/70 to-[#0a0a0a]/30" />
          <div className="absolute inset-0 bg-[#0a0a0a]/30" />
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 pb-16 md:pb-24 w-full">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }}
            className="font-serif-accent italic text-[#ffd100] text-xl md:text-2xl mb-4"
          >
            Ottawa&apos;s beloved Indian kitchen since {business.since}
          </motion.p>

          <h1 className="font-display font-bold text-white leading-[0.95] text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-tight">
            <MaskLine delay={0.15}>Authentic</MaskLine>
            <MaskLine delay={0.3}>
              Indian Food, <span className="text-[#ffd100]">Made</span>
            </MaskLine>
            <MaskLine delay={0.45}>With Heart.</MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.8 }}
            className="mt-6 max-w-xl text-neutral-200 text-lg md:text-xl leading-relaxed"
          >
            Family recipes, whole spices, and warm hospitality on Merivale Road.
            Veg, Vegan, Halal & Non-Veg — a table for everyone.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8 }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <Link to="/menu" data-testid="hero-menu-btn"
              className="group inline-flex items-center justify-center gap-2 bg-[#ffd100] text-[#0a0a0a] font-bold text-lg px-8 py-4 rounded-full hover:bg-[#e5bc00] transition-colors">
              View Our Menu
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <button type="button" onClick={openOrder} data-testid="hero-order-btn"
              className="inline-flex items-center justify-center gap-2 border-2 border-[#ffd100] text-[#ffd100] font-bold text-lg px-8 py-4 rounded-full hover:bg-[#ffd100] hover:text-[#0a0a0a] transition-colors">
              Order Online
            </button>
            <Link to="/catering" data-testid="hero-catering-btn"
              className="inline-flex items-center justify-center gap-2 border-2 border-white/70 text-white font-bold text-lg px-8 py-4 rounded-full hover:bg-white hover:text-[#0a0a0a] hover:border-white transition-colors">
              Book Catering
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ============ DIETARY BADGES ============ */}
      <section className="bg-[#ffd100] relative z-10">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-8 md:py-10">
          <StaggerGroup className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {badges.map(({ icon: Icon, label }) => (
              <StaggerItem key={label} className="flex items-center gap-3 justify-center md:justify-start">
                <Icon size={30} strokeWidth={2.4} className="text-[#0a0a0a]" />
                <span className="font-display font-semibold text-[#0a0a0a] text-lg md:text-xl">{label}</span>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* ============ FEATURED COMBOS ============ */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28">
        <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-[#ffd100] font-semibold uppercase tracking-[0.2em] text-sm mb-3">Popular Right Now</p>
            <h2 className="font-display font-semibold text-4xl md:text-5xl text-white tracking-tight">
              Combo Meals Everyone Loves
            </h2>
          </div>
          <Link to="/menu/combos" className="text-[#ffd100] font-medium text-lg inline-flex items-center gap-2 hover:gap-3 transition-all">
            See full menu <ArrowRight size={20} />
          </Link>
        </Reveal>

        <StaggerGroup className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
          {combos.map((c) => (
            <StaggerItem key={c.name}>
              <ComboCard c={c} />
            </StaggerItem>
          ))}
        </StaggerGroup>

        <Reveal className="mt-10">
          <ChooseYourCurry />
        </Reveal>
      </section>

      {/* ============ EDITORIAL MARQUEE ============ */}
      <section className="border-y border-white/10 bg-[#141414] py-6 overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex items-center shrink-0">
              {["Freshly Ground Spices", "Since 1995", "100% Halal", "Made With Heart", "Ottawa's Favourite", "Veg • Vegan • Non-Veg"].map((t) => (
                <span key={t} className="flex items-center font-display text-3xl md:text-4xl font-medium text-white/80 mx-8">
                  {t} <Star size={20} className="text-[#ffd100] ml-8 fill-[#ffd100]" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* ============ ABOUT TEASER ============ */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <p className="text-[#ffd100] font-semibold uppercase tracking-[0.2em] text-sm mb-4">Our Story</p>
            <h2 className="font-display font-semibold text-4xl md:text-5xl text-white leading-tight tracking-tight">
              Serving Ottawa for nearly 30 years.
            </h2>
            <p className="mt-6 text-neutral-300 text-lg leading-relaxed">
              Since {business.since}, Shaan Curry House has brought the warm, home-cooked
              flavours of India to Merivale Road. Every curry is built from whole spices we
              grind in-house and recipes passed down through generations.
            </p>
            <p className="mt-4 text-neutral-400 text-lg leading-relaxed">
              From comforting family dinners to grand celebrations, we cook every plate with
              the same care we would for our own table.
            </p>
            <Link to="/about" data-testid="home-about-btn"
              className="mt-8 inline-flex items-center gap-2 border-2 border-white/20 text-white font-medium px-7 py-3.5 rounded-full hover:border-[#ffd100] hover:text-[#ffd100] transition-colors">
              Read our story <ArrowRight size={18} />
            </Link>
          </Reveal>

          <Reveal delay={0.15} className="relative">
            <div className="grid grid-cols-2 gap-4">
              <img src={assets.gallery[0]} alt="Shaan Curry House dish" loading="lazy"
                className="rounded-2xl h-64 w-full object-cover mt-8" />
              <img src={assets.gallery[3]} alt="Inside the restaurant" loading="lazy"
                className="rounded-2xl h-64 w-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[#ffd100] text-[#0a0a0a] rounded-2xl px-6 py-4 shadow-xl">
              <p className="font-display text-4xl font-bold leading-none">{business.since}</p>
              <p className="text-sm font-semibold mt-1">Est. in Ottawa</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ GOOGLE REVIEWS ============ */}
      <GoogleReviews />

      {/* ============ CTA ============ */}
      <section className="relative overflow-hidden">
        <img src={assets.gallery[8]} alt="Catering spread" loading="lazy"
          className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0a0a0a]/85" />
        <div className="relative z-10 max-w-4xl mx-auto px-5 md:px-10 py-24 md:py-32 text-center">
          <Reveal>
            <h2 className="font-display font-bold text-4xl md:text-6xl text-white tracking-tight">
              Planning a celebration?
            </h2>
            <p className="mt-5 text-neutral-200 text-lg md:text-xl">
              Let us cater your next event with a menu your guests will remember.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/catering" data-testid="cta-catering-btn"
                className="inline-flex items-center justify-center gap-2 bg-[#ffd100] text-[#0a0a0a] font-bold text-lg px-8 py-4 rounded-full hover:bg-[#e5bc00] transition-colors">
                Explore Catering
              </Link>
              <a href={`tel:${business.phoneRaw}`} data-testid="cta-call-btn"
                className="inline-flex items-center justify-center gap-2 border-2 border-white text-white font-bold text-lg px-8 py-4 rounded-full hover:bg-white hover:text-[#0a0a0a] transition-colors">
                Call {business.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
